import type { ContactPoint } from 'schema-dts';

import { config } from './config';
import type {
    ContactPointInput,
    ContactPointResult,
    ContactPointValidationError,
    GeneratorOptions,
} from './types/company';
import { stripHtmlTags, Result, ok, fail, isOk, safeString, isValidUrl, LRUCache } from './utils';
import { logger } from './utils/logger';

const contactPointCache = new LRUCache<string, ContactPoint>({
    maxEntries: config.getConfig().cache.maxDurationEntries,
});

function validateContactPointInput(input: unknown): Result<ContactPointInput, ContactPointValidationError[]> {
    const errors: ContactPointValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'contactType', message: 'ContactPoint input must be an object' }]);
    }

    const contactPoint = input as Record<string, unknown>;

    if (!contactPoint.contactType || typeof contactPoint.contactType !== 'string') {
        errors.push({
            field: 'contactType',
            message: 'Contact type is required and must be a string',
            value: contactPoint.contactType,
        });
    }

    if (contactPoint.email && typeof contactPoint.email === 'string') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contactPoint.email)) {
            errors.push({
                field: 'email',
                message: 'Invalid email format',
                value: contactPoint.email,
            });
        }
    }

    if (contactPoint.url && typeof contactPoint.url === 'string') {
        if (!isValidUrl(contactPoint.url)) {
            errors.push({
                field: 'url',
                message: 'Invalid URL format',
                value: contactPoint.url,
            });
        }
    }

    if (contactPoint.availableLanguages !== undefined) {
        if (!Array.isArray(contactPoint.availableLanguages)) {
            errors.push({
                field: 'availableLanguages',
                message: 'Available languages must be an array',
                value: contactPoint.availableLanguages,
            });
        } else {
            contactPoint.availableLanguages.forEach((lang, index) => {
                if (typeof lang !== 'string') {
                    errors.push({
                        field: 'availableLanguages',
                        message: `Language at index ${index} must be a string`,
                        value: lang,
                    });
                }
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(contactPoint as unknown as ContactPointInput);
}

class ContactPointBuilder {
    private contactPoint: Partial<ContactPoint>;

    constructor(contactType: string) {
        this.contactPoint = {
            '@type': 'ContactPoint',
            contactType: stripHtmlTags(safeString(contactType)),
        };
    }

    setTelephone(phone?: string): ContactPointBuilder {
        if (phone) {
            this.contactPoint.telephone = stripHtmlTags(safeString(phone));
        }
        return this;
    }

    setEmail(email?: string): ContactPointBuilder {
        if (email) {
            this.contactPoint.email = stripHtmlTags(safeString(email));
        }
        return this;
    }

    setFaxNumber(fax?: string): ContactPointBuilder {
        if (fax) {
            this.contactPoint.faxNumber = stripHtmlTags(safeString(fax));
        }
        return this;
    }

    setUrl(url?: string): ContactPointBuilder {
        if (url) {
            this.contactPoint.url = stripHtmlTags(safeString(url));
        }
        return this;
    }

    setAreaServed(area?: string): ContactPointBuilder {
        if (area) {
            this.contactPoint.areaServed = stripHtmlTags(safeString(area));
        }
        return this;
    }

    setAvailableLanguages(languages?: string[]): ContactPointBuilder {
        if (languages && languages.length > 0) {
            this.contactPoint.availableLanguage = languages.map((lang) => stripHtmlTags(safeString(lang)));
        }
        return this;
    }

    setHoursAvailable(hours?: string): ContactPointBuilder {
        if (hours) {
            logger.debug('Skipping hoursAvailable - requires OpeningHoursSpecification parsing', { hours });
        }
        return this;
    }

    setProductSupported(product?: string): ContactPointBuilder {
        if (product) {
            this.contactPoint.productSupported = stripHtmlTags(safeString(product));
        }
        return this;
    }

    build(): ContactPoint {
        if (!this.contactPoint.contactType) {
            throw new Error('ContactPoint must have a contactType');
        }

        return this.contactPoint as ContactPoint;
    }
}

export function generateContactPointSchema(
    input: ContactPointInput,
    options: GeneratorOptions = {},
): Result<ContactPointResult> {
    const { useCache = true, validateInput = true } = options;

    const cacheKey = useCache ? JSON.stringify(input) : '';

    if (useCache && cacheKey) {
        const cached = contactPointCache.get(cacheKey);
        if (cached) {
            logger.debug('ContactPoint schema cache hit', { input });
            return ok(cached);
        }
    }

    if (validateInput) {
        const validation = validateContactPointInput(input);
        if (!isOk(validation)) {
            logger.warn('Invalid ContactPoint input', { errors: validation.error });
            return fail(new Error(`Invalid ContactPoint input: ${validation.error.map((e) => e.message).join(', ')}`));
        }
    }

    try {
        const builder = new ContactPointBuilder(input.contactType);

        const contactPoint = builder
            .setTelephone(input.telephone)
            .setEmail(input.email)
            .setFaxNumber(input.faxNumber)
            .setUrl(input.url)
            .setAreaServed(input.areaServed)
            .setAvailableLanguages(input.availableLanguages)
            .setHoursAvailable(input.hoursAvailable)
            .setProductSupported(input.productSupported)
            .build();

        if (useCache && cacheKey) {
            contactPointCache.set(cacheKey, contactPoint);
        }

        logger.info('Generated ContactPoint schema', {
            contactType: input.contactType,
            hasEmail: !!input.email,
            hasPhone: !!input.telephone,
            cached: useCache,
        });

        return ok(contactPoint);
    } catch (error) {
        logger.error('Failed to generate ContactPoint schema', { error, input });
        return fail(error instanceof Error ? error : new Error('Unknown error generating ContactPoint schema'));
    }
}
